import { useMutation } from '@apollo/react-hooks';
import { useContext, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import updateAssignedContentStatus from '../../../apollo/mutations/updateAssignedContentStatus.graphql';
import updateContentAssignment from '../../../apollo/mutations/updateContentAssignment.graphql';
import assignSections from '../../../apollo/mutations/assignSections.graphql';
import unassignSections from '../../../apollo/mutations/unassignSections.graphql';
import AssignmentContext from '../../common/AssignmentContext/AssignmentContext';
import { useErrorHandler } from '../../common/ErrorHandler/ErrorHandler';
import Spinner from '../../common/Spinner/Spinner';
import assignmentTypes from '../../../utils/assignmentTypes';
import fireNotification from '../../../utils/fireNotification';
import { useLanguage } from '../../../utils/language';
import { isNcleAlefAssignment } from '../../../utils/isNcleAlefAssignment';
import { StudentListContext } from './StudentListContext';
import {
  ASSIGN_UNASSIGN_STATUS_MESSAGES,
  LOCK_UNLOCK_STATUS_MESSAGES,
} from './assignStatusNotificationMessages';

export const useUpdateAssignStatusToAssignment = () => {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const { langCode } = useLanguage();

  const [
    assignStudentsNotificationData,
    setAssignStudentsNotificationData,
  ] = useState({
    isIndividualAction: false,
    students: [],
    assignStatus: '',
  });

  const {
    assignmentParams: {
      assignmentId,
      mloId,
      classId,
      contentId,
      assignmentInstanceId,
      academicYearId,
      contentType,
      organisationName,
      groupId,
      trimesterOrder,
      isGradableItemsLockedByDefault,
      isNcle,
    },
    assignmentData: { assignmentType },
    methods: { refetchClassAssignmentList },
  } = useContext(AssignmentContext);

  const {
    setIsFailedLockModalOpen,
    refetchingClassAssignmentListInProgress,
    setRefetchingClassAssignmentListInProgress,
  } = useContext(StudentListContext);

  const shouldUseSectionsMutation = isNcleAlefAssignment(
    isNcle,
    assignmentType,
  );

  const fireNotificationForAssignStudents = ({
    isIndividualAction,
    students,
    assignStatus,
  }) => {
    const statusMessages = shouldUseSectionsMutation
      ? ASSIGN_UNASSIGN_STATUS_MESSAGES
      : LOCK_UNLOCK_STATUS_MESSAGES;

    const messages = statusMessages[assignStatus];
    const studentName = students[0].name;

    if (isIndividualAction) {
      fireNotification(
        'unlockedStudentByName',
        langCode,
        t(messages.individual, {
          STUDENTS_COUNT: studentName,
          STUDENT_NAME: studentName,
        }),
      );
      return;
    }

    if (students.length === 1) {
      fireNotification(
        'unlockedStudentByCount',
        langCode,
        t(messages.single, { STUDENTS_COUNT: 1 }),
      );
      return;
    }

    if (students.length > 1) {
      fireNotification(
        'unlockedStudentByCount',
        langCode,
        t(messages.multiple, { STUDENTS_COUNT: students.length }),
      );
    }
  };

  const refreshClassAssignmentList = async () => {
    setIsFailedLockModalOpen(false);
    setRefetchingClassAssignmentListInProgress(true);
    await refetchClassAssignmentList({
      input: {
        type: assignmentType,
        assignmentId,
        lessonId:
          assignmentType === assignmentTypes.TEACHER ? mloId : undefined,
        referrerId: assignmentInstanceId || mloId,
        classId,
        contentId,
        isGradableItemsLockedByDefault,
      },
    });
    setRefetchingClassAssignmentListInProgress(false);
  };

  const [
    updateAssignedContentStudents,
    { loading: unAssigningStudentsInProgress },
  ] = useMutation(updateAssignedContentStatus, {
    onCompleted: async data => {
      if (data.updateAssignedContentStatus.failedAssignmentsToLockCount > 0) {
        setIsFailedLockModalOpen(true);
      } else {
        await refreshClassAssignmentList();
      }
      fireNotificationForAssignStudents(assignStudentsNotificationData);
    },
    onError: () => {
      handleError();
    },
  });

  function wasNotUnAssigned(updatedActivityComponents) {
    const assignmentComponent = updatedActivityComponents.find(
      component => component.contentId === contentId,
    );
    return (
      assignStudentsNotificationData.assignStatus === 'UN_ASSIGNED' &&
      assignmentComponent &&
      !assignmentComponent.isLocked
    );
  }

  const [
    updateContentAssignmentStatusForStudents,
    { loading: updateContentAssignmentStatusInProgress },
  ] = useMutation(updateContentAssignment, {
    onCompleted: async data => {
      if (
        wasNotUnAssigned(
          data.updateContentAssignment.updatedActivityComponents,
        ) ||
        data.updateContentAssignment.errors.length > 0
      ) {
        setIsFailedLockModalOpen(true);
      } else {
        await refreshClassAssignmentList();
      }
      fireNotificationForAssignStudents(assignStudentsNotificationData);
    },
    onError: () => {
      handleError();
    },
  });

  const [
    assignSectionsMutation,
    { loading: assigningSectionsInProgress },
  ] = useMutation(assignSections, {
    onCompleted: async data => {
      if (data.assignSections.failedCount > 0) {
        setIsFailedLockModalOpen(true);
      } else {
        await refreshClassAssignmentList();
        fireNotificationForAssignStudents(assignStudentsNotificationData);
      }
    },
    onError: () => {
      handleError();
    },
  });

  const [
    unassignSectionsMutation,
    { loading: unassigningSectionsInProgress },
  ] = useMutation(unassignSections, {
    onCompleted: async data => {
      if (data.unassignSections.failedCount > 0) {
        setIsFailedLockModalOpen(true);
      } else {
        await refreshClassAssignmentList();
        fireNotificationForAssignStudents(assignStudentsNotificationData);
      }
    },
    onError: () => {
      handleError();
    },
  });

  const updateSectionsAssignStatus = async (students, assignStatus) => {
    const studentIds = students.map(student => student.id);

    if (assignStatus === 'ASSIGNED') {
      await assignSectionsMutation({
        variables: {
          input: {
            classId,
            studentIds,
            activities: [
              {
                activityId: mloId,
                components: [
                  {
                    componentId: contentId,
                    sectionId: contentId,
                  },
                ],
              },
            ],
          },
        },
      });
      return;
    }

    await unassignSectionsMutation({
      variables: {
        input: {
          classId,
          studentIds,
          activityId: mloId,
          componentId: contentId,
          sectionId: contentId,
        },
      },
    });
  };

  const onUpdateAssignStatusStudents = (
    students,
    assignStatus,
    isIndividualAction = false,
  ) => async e => {
    e.preventDefault();
    e.stopPropagation();

    if (
      unAssigningStudentsInProgress ||
      assigningSectionsInProgress ||
      unassigningSectionsInProgress
    )
      return;

    setAssignStudentsNotificationData({
      students,
      isIndividualAction,
      assignStatus,
    });

    if (shouldUseSectionsMutation) {
      await updateSectionsAssignStatus(students, assignStatus);
      return;
    }

    if (assignmentType === assignmentTypes.TEACHER) {
      await updateAssignedContentStudents({
        variables: {
          input: {
            lessonId: mloId,
            learnerIds: students.map(student => student.id),
            teamIds: [],
            classId,
            mloContents: [
              {
                contentId,
                contentType: contentType || 'ASGN',
                assignStatus,
                assignmentId,
              },
            ],
            academicYearId,
            isGradableItemsLocked: isGradableItemsLockedByDefault,
          },
        },
      });
    } else {
      await updateContentAssignmentStatusForStudents({
        variables: {
          input: {
            activityId: mloId,
            studentIds: students.map(student => student.id),
            classId,
            academicYearId,
            organisationName,
            trimesterOrder,
            groupId,
            contentId,
            assignStatus,
          },
          classId,
        },
      });
    }
  };

  return {
    onUpdateAssignStatusStudents,
    updatingStatusInProgress:
      refetchingClassAssignmentListInProgress ||
      unAssigningStudentsInProgress ||
      updateContentAssignmentStatusInProgress ||
      assigningSectionsInProgress ||
      unassigningSectionsInProgress,
    refreshClassAssignmentList,
  };
};

export const LockUnlockIconSpinner = styled(Spinner)`
  margin-right: 10px !important;
`;
